import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { companyAPI } from '../services/api';
import Layout from '../components/Layout';
import Icon from '../components/Icon';
import { DEFAULT_APP_LOGO } from '../constants';


// LeafletMap component for rendering interactive map with markers
function LeafletMap({ companies, centerLat, centerLng, mapRef, mapInstance, setMapInstance }) {
  // Use refs to store the map instance and markers
  const localMapRef = useRef(null);
  const markersRef = useRef([]);

  // Initialize map only once
  useEffect(() => {
    // Check if Leaflet is available and container exists
    if (!mapRef.current || typeof window === 'undefined' || !window.L) {
      return;
    }

    // If the container still has a stale Leaflet instance (can happen when the
    // map is unmounted during search/loading), clean it up first so Leaflet
    // doesn't throw "Map container is being reused by another instance".
    if (!localMapRef.current && mapRef.current._leaflet_id) {
      try {
        if (mapRef.current.__leafletMap) {
          mapRef.current.__leafletMap.remove();
        }
      } catch (cleanupError) {
        console.warn('Could not clean stale Leaflet map instance', cleanupError);
      } finally {
        mapRef.current.innerHTML = '';
        delete mapRef.current._leaflet_id;
        delete mapRef.current.__leafletMap;
      }
    }

    // Don't initialize if already initialized
    if (localMapRef.current) {
      return;
    }

    try {
      // Create map instance with scroll wheel zoom disabled (prevents accidental zoom on Mac)
      const map = window.L.map(mapRef.current, {
        scrollWheelZoom: false, // Disable scroll wheel zoom to prevent accidental zoom
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
        touchZoom: true
      }).setView([centerLat, centerLng], 10);
      localMapRef.current = map;
      mapRef.current.__leafletMap = map;

      // Add tile layer
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);
      
      // Add zoom controls
      window.L.control.zoom({
        position: 'topright'
      }).addTo(map);

      // Update parent component's map instance
      if (setMapInstance) {
        setMapInstance(map);
      }
    } catch (error) {
      console.error('Error initializing Leaflet map:', error);
    }

    // Cleanup function: remove map when component unmounts
    return () => {
      // Clear markers first
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          try {
            marker.remove();
          } catch (e) {
            // Ignore errors during cleanup
          }
        });
        markersRef.current = [];
      }
      
      // Remove map
      if (localMapRef.current) {
        try {
          localMapRef.current.remove();
          localMapRef.current = null;
        } catch (error) {
          console.error('Error cleaning up Leaflet map:', error);
        }
      }
      
      // Clear the container
      if (mapRef.current) {
        mapRef.current.innerHTML = '';
        delete mapRef.current._leaflet_id;
        delete mapRef.current.__leafletMap;
      }
      
      if (setMapInstance) {
        setMapInstance(null);
      }
    };
  }, []); // Only initialize once on mount

  // Update markers when companies change
  useEffect(() => {
    if (!localMapRef.current || typeof window === 'undefined' || !window.L) {
      return;
    }

    const map = localMapRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      try {
        marker.remove();
      } catch (e) {
        // Ignore errors
      }
    });
    markersRef.current = [];

    // Add new markers
    const markers = [];
    companies.forEach((company, idx) => {
      const lat = parseFloat(company.latitude);
      const lng = parseFloat(company.longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        const icon = window.L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            background: linear-gradient(135deg, #1A2811, #4A8C18);
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 14px;
            color: #1a1a2e;
            border: 2px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">${idx + 1}</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });

        const marker = window.L.marker([lat, lng], { icon }).addTo(map);
        const logoUrl = company.logo_url || DEFAULT_APP_LOGO;
        const logoHtml = `<div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;">
              <img src="${logoUrl}" alt="${company.name}" style="width:40px;height:40px;border-radius:8px;object-fit:contain;background:rgba(0,0,0,0.1);"/>
              <strong style="font-size:15px;">${company.name}</strong>
            </div>`;
        const popupContent = `
          <div style="min-width: 220px; padding: 4px;">
            ${logoHtml}
            ${company.address && company.address.street ? `<div style="color:#666;font-size:13px;margin-bottom:2px;">${company.address.street}</div>` : ''}
            ${company.address && company.address.city ? `<div style="color:#666;font-size:13px;margin-bottom:6px;">${company.address.city}</div>` : ''}
            ${(company.average_rating || company.avg_rating) ? `<div style="display:inline-flex;align-items:center;gap:4px;padding:4px 8px;background:#fff8e1;border-radius:12px;font-size:13px;font-weight:600;color:#f59e0b;"><span class="material-symbols-rounded" style="font-size:14px;vertical-align:middle;">star</span> ${((company.average_rating || company.avg_rating) || 0).toFixed(1)}</div>` : ''}
            <div style="margin-top:10px;text-align:center;font-size:12px;color:#3d5a20;font-weight:500;">Click to view & connect</div>
          </div>
        `;
        marker.bindPopup(popupContent);
        marker.on('click', () => {
          window.location.href = `/companies/${company.id}`;
        });
        markers.push(marker);
      }
    });

    markersRef.current = markers;

    // Fit map to markers bounds
    if (markers.length > 0) {
      const group = new window.L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.1));
    }
  }, [companies]); // Update markers when companies change

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '500px', borderRadius: '0 0 16px 16px' }}
      onWheel={(e) => {
        // Prevent map zoom on scroll (especially for Mac trackpad)
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
        }
      }}
      onTouchStart={(e) => {
        // Prevent accidental zoom on mobile
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      }}
    />
  );
}

function CompanyListPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Debounced search value
  const [sort, setSort] = useState('name');
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const mapRef = useRef(null);
  const fetchAbortRef = useRef(null);

  // Debounce search input - only update searchQuery after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(search);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch companies when searchQuery, sort, lat, or lng changes
  useEffect(() => {
    // Cancel any pending fetch
    if (fetchAbortRef.current) {
      fetchAbortRef.current = false;
    }
    fetchCompanies();
  }, [sort, lat, lng, searchQuery]);

  // Note: Map cleanup is handled inside LeafletMap component's useEffect cleanup.
  // The mapInstance state is only for external reference (e.g., flying to markers).

  const fetchCompanies = async () => {
    const fetchId = Date.now();
    fetchAbortRef.current = fetchId;
    
    try {
      setLoading(true);
      setError('');
      const resp = await companyAPI.getCompanyList({ search: searchQuery, sort, limit: 50, lat, lng });
      
      // Check if this fetch is still relevant (not superseded by a newer one)
      if (fetchAbortRef.current !== fetchId) {
        return;
      }
      
      const companiesList = resp.data.companies || [];
      setCompanies(companiesList);
      if (companiesList.length === 0 && !searchQuery) {
        setError('No companies found');
      }
    } catch (err) {
      // Check if this fetch is still relevant
      if (fetchAbortRef.current !== fetchId) {
        return;
      }
      console.error('Error loading companies:', err);
      const errorMsg = err.response?.data?.error || 'Failed to load companies';
      setError(errorMsg);
      setCompanies([]);
    } finally {
      // Only set loading to false if this is the latest fetch
      if (fetchAbortRef.current === fetchId) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Immediately update searchQuery to trigger search
    setSearchQuery(search);
  };

  return (
    <Layout user={user} onLogout={onLogout} pageTitle="Find Service Companies" pageSubtitle="Browse active partners near you" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      {/* Search & Filter */}
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by name, city or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input"
            style={{ flex: '1', minWidth: '200px' }}
          />
          <select 
            value={sort} 
            onChange={(e) => {
              e.preventDefault();
              const newSort = e.target.value;
              setSort(newSort);
              // Don't trigger fetch here - useEffect will handle it
            }} 
            className="form-input" 
            style={{ minWidth: '160px' }}
          >
            <option value="name">Sort by name</option>
            <option value="rating">Sort by rating</option>
            <option value="distance">Sort by distance</option>
          </select>
          <button type="submit" className="btn btn-primary">Search</button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    setLat(pos.coords.latitude);
                    setLng(pos.coords.longitude);
                    setSort('distance');
                    setError(''); // Clear any previous errors
                  },
                  (err) => {
                    if (err.code === err.PERMISSION_DENIED) {
                      setError('Location access denied. Please enable location permissions in your browser settings to use this feature.');
                    } else if (err.code === err.POSITION_UNAVAILABLE) {
                      setError('Location information is unavailable. Please check your device settings.');
                    } else if (err.code === err.TIMEOUT) {
                      setError('Location request timed out. Please try again.');
                    } else {
                      setError('Failed to get your location. Please try again.');
                    }
                  },
                  {
                    enableHighAccuracy: false,
                    timeout: 10000,
                    maximumAge: 300000 // 5 minutes
                  }
                );
              } else {
                setError('Location services are not supported by your browser.');
              }
            }}
          >
            <Icon name="location_on" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Use my location
          </button>
        </form>
      </div>

      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}

      {/* Map Section */}
      {!loading && companies.length > 0 && companies.some(c => {
        const hasLat = c.latitude !== null && c.latitude !== undefined;
        const hasLng = c.longitude !== null && c.longitude !== undefined;
        return hasLat && hasLng;
      }) && (
        <div style={{
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          overflow: 'hidden',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h3 style={{ margin: 0, color: '#ffffff', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="map" color="primary" darkMode={darkMode} size={18} />
              Service Shops on Map
            </h3>
            <button 
              onClick={() => setShowMap(!showMap)}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>
          
          {showMap && (
            <div style={{ 
              position: 'relative', 
              height: window.innerWidth <= 768 ? '400px' : '500px', 
              background: '#1a1a2e', 
              borderRadius: '0 0 16px 16px'
            }}>
              {(() => {
                // Calculate map bounds to fit all companies
                const companiesWithCoords = companies.filter(c => {
                  const hasLat = c.latitude !== null && c.latitude !== undefined;
                  const hasLng = c.longitude !== null && c.longitude !== undefined;
                  return hasLat && hasLng;
                });
                if (companiesWithCoords.length === 0) {
                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'rgba(255,255,255,0.5)'
                    }}>
                      No map data available
                    </div>
                  );
                }
                
                // Calculate center and bounds
                const lats = companiesWithCoords.map(c => parseFloat(c.latitude));
                const lngs = companiesWithCoords.map(c => parseFloat(c.longitude));
                const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                const minLat = Math.min(...lats) - 0.05;
                const maxLat = Math.max(...lats) + 0.05;
                const minLng = Math.min(...lngs) - 0.05;
                const maxLng = Math.max(...lngs) + 0.05;
                
                // Use Leaflet via CDN for better interactivity
                const useLeaflet = typeof window !== 'undefined' && window.L;
                
                if (useLeaflet) {
                  // Leaflet implementation with markers for each company
                  return (
                    <LeafletMap
                      key={`map-${companiesWithCoords.map(c => c.id).join('-')}`}
                      companies={companiesWithCoords}
                      centerLat={centerLat}
                      centerLng={centerLng}
                      mapRef={mapRef}
                      mapInstance={mapInstance}
                      setMapInstance={setMapInstance}
                    />
                  );
                }
                
                // Fallback to OpenStreetMap iframe if Leaflet not available
                const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
                const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${centerLat},${centerLng}`;
                
                return (
                  <>
                    <iframe
                      title="Service Shops Map"
                      width="100%"
                      height="500"
                      frameBorder="0"
                      src={osmUrl}
                      style={{ display: 'block', border: 'none' }}
                      allowFullScreen
                    />
                    {/* Overlay with company markers */}
                    <div style={{
                      position: 'absolute',
                      bottom: '12px',
                      left: '12px',
                      right: '12px',
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      pointerEvents: 'auto',
                      maxHeight: window.innerWidth <= 768 ? '120px' : '150px',
                      overflowY: 'auto',
                      padding: window.innerWidth <= 768 ? '8px' : '0'
                    }}>
                      {companiesWithCoords.map((company, idx) => (
                        <Link
                          key={company.id}
                          to={`/companies/${company.id}`}
                          style={{
                            padding: '10px 14px',
                            background: selectedCompanyId === company.id ? 'rgba(61, 90, 32, 0.95)' : 'rgba(26, 26, 46, 0.95)',
                            color: selectedCompanyId === company.id ? '#1a1a2e' : 'white',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '500',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            border: selectedCompanyId === company.id ? '2px solid #3d5a20' : '1px solid rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: 'var(--shadow-md)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={() => setSelectedCompanyId(company.id)}
                          onMouseLeave={() => setSelectedCompanyId(null)}
                        >
                          <span style={{ 
                            width: '24px', 
                            height: '24px', 
                            background: 'linear-gradient(135deg, #1A2811, #4A8C18)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#1a1a2e',
                            flexShrink: 0
                          }}>
                            {idx + 1}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: '600' }}>{company.name}</span>
                            {(company.average_rating || company.avg_rating) && (
                              <span style={{ 
                                color: selectedCompanyId === company.id ? '#1a1a2e' : '#fbbf24',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <Icon name="star" color="warning" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle' }} />
                                {((company.average_rating || company.avg_rating) || 0).toFixed(1)}
                                {company.review_count > 0 && (
                                  <span style={{ opacity: 0.8 }}>({company.review_count})</span>
                                )}
                              </span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading companies...</div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {companies.map((company, idx) => {
            const hasCoords = company.latitude !== null && company.latitude !== undefined && 
                             company.longitude !== null && company.longitude !== undefined;
            const mapIndex = hasCoords ? companies.filter((c, i) => {
              const cHasCoords = c.latitude !== null && c.latitude !== undefined && 
                                c.longitude !== null && c.longitude !== undefined;
              return i < idx && cHasCoords;
            }).length + 1 : null;
            
            return (
            <Link 
              key={company.id} 
              to={`/companies/${company.id}`}
              style={{ textDecoration: 'none' }}
              onMouseEnter={() => setSelectedCompanyId(company.id)}
              onMouseLeave={() => setSelectedCompanyId(null)}
            >
              <div style={{
                background: selectedCompanyId === company.id ? 'rgba(61, 90, 32, 0.08)' : 'rgba(255,255,255,0.03)',
                border: selectedCompanyId === company.id ? '1px solid rgba(61, 90, 32, 0.3)' : '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
                padding: '20px',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(61, 90, 32, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(61, 90, 32, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
              }}
              >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      {/* Map marker number */}
                      {mapIndex && mapIndex <= 5 && (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          background: 'linear-gradient(135deg, #1A2811, #4A8C18)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#1a1a2e',
                          flexShrink: 0,
                          marginTop: '2px'
                        }}>
                          {mapIndex}
                        </div>
                      )}
                      {/* Company logo (or default app logo when no logo uploaded) */}
                      <div
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          overflow: 'hidden',
                          background: 'rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <img
                          src={company.logo_url || DEFAULT_APP_LOGO}
                          alt={`${company.name} logo`}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div>
                        <h3 style={{ margin: '0 0 8px 0', color: '#ffffff', fontSize: '18px' }}>{company.name}</h3>
                        {company.email && (
                          <p style={{ margin: '0 0 4px 0', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                            <Icon name="email" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            {company.email}
                          </p>
                        )}
                        {company.phone && (
                          <p style={{ margin: '0 0 4px 0', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                            <Icon name="phone" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            {company.phone}
                          </p>
                        )}
                        {company.address && (
                          <p style={{ margin: '0', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                            <Icon name="location_on" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            {company.address.city || ''} {company.address.street || ''}
                          </p>
                        )}
                      </div>
                    </div>
                  <div style={{ textAlign: 'right' }}>
                    {(company.average_rating || company.avg_rating) && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        background: 'rgba(245, 158, 11, 0.15)',
                        borderRadius: '20px',
                        color: '#fbbf24',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <Icon name="star" customColor="#fbbf24" size={16} /> {((company.average_rating || company.avg_rating) || 0).toFixed(1)}
                        {company.review_count && company.review_count > 0 && (
                          <span style={{ fontWeight: 'normal', opacity: 0.8 }}>
                            ({company.review_count} {company.review_count === 1 ? 'review' : 'reviews'})
                          </span>
                        )}
                      </div>
                    )}
                    {!company.average_rating && !company.avg_rating && company.review_count === 0 && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        background: 'var(--bg-overlay-medium)',
                        borderRadius: '20px',
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '13px'
                      }}>
                        No reviews yet
                      </div>
                    )}
                    {company.distance_km && (
                      <div style={{ marginTop: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                        {company.distance_km.toFixed(1)} km away
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
          })}
          {companies.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              <Icon name="search" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
              <p>No companies found</p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

export default CompanyListPage;
